/**
 * Utility functions for handling multiple medication interactions per supplement
 * Provides functions for processing, prioritizing, and formatting interaction data
 */

/**
 * Risk priority levels for interaction comparison
 */
const RISK_PRIORITY = {
  'High': 3,
  'Medium': 2,
  'Low': 1,
  'None': 0
};

/**
 * Selects the highest risk interaction from an array of interactions
 * @param {Array} interactions - Array of interaction objects
 * @returns {Object|null} - Interaction with highest risk, or null if no valid interactions
 */
export const selectHighestRiskInteraction = (interactions) => {
  if (!interactions || !Array.isArray(interactions) || interactions.length === 0) {
    return null;
  }

  // Filter out invalid interactions
  const validInteractions = interactions.filter(interaction => 
    interaction && 
    interaction.risk && 
    typeof interaction.risk === 'string'
  );

  if (validInteractions.length === 0) {
    return null;
  }

  // Find the interaction with highest risk priority
  return validInteractions.reduce((highest, current) => {
    const currentPriority = RISK_PRIORITY[current.risk] || 0;
    const highestPriority = RISK_PRIORITY[highest.risk] || 0;
    
    // Return current if it has higher priority, otherwise keep highest
    return currentPriority > highestPriority ? current : highest;
  });
};

/**
 * Groups interactions by supplement key from API response
 * @param {Array} interactions - Array of interactions from API response
 * @returns {Object} - Object with supplement_key as keys, arrays of interactions as values
 * 
 * Example output:
 * {
 *   "turmeric": [interaction1, interaction2, ...],
 *   "berberine": [interaction1, ...]
 * }
 */
export const groupInteractionsBySupplementKey = (interactions) => {
  if (!interactions || !Array.isArray(interactions)) {
    return {};
  }

  const grouped = {};
  
  interactions.forEach(interaction => {
    if (!interaction || !interaction.supplement_key) {
      return; // Skip invalid interactions
    }

    const key = interaction.supplement_key;
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(interaction);
  });

  return grouped;
};

/**
 * Calculates aggregate risk level from multiple interactions
 * Uses logic: Any High = High, Multiple Mediums = High, Any Medium = Medium, otherwise Low
 * @param {Array} interactions - Array of interaction objects
 * @returns {string} - Aggregate risk level ('High', 'Medium', 'Low', or 'None')
 */
export const calculateAggregateRisk = (interactions) => {
  if (!interactions || !Array.isArray(interactions) || interactions.length === 0) {
    return 'None';
  }

  const risks = interactions
    .filter(interaction => interaction && interaction.risk)
    .map(interaction => interaction.risk);

  if (risks.length === 0) {
    return 'None';
  }

  // Simply use the highest risk from any single supplement
  if (risks.includes('High')) {
    return 'High';
  }

  if (risks.includes('Medium')) {
    return 'Medium';
  }

  if (risks.includes('Low')) {
    return 'Low';
  }

  return 'None';
};

/**
 * Formats a user-friendly summary of multiple interactions
 * @param {Array} interactions - Array of interaction objects
 * @param {Function} getMedicationName - Function to get friendly medication name from rxcui
 * @returns {string} - Formatted summary text
 */
export const formatInteractionSummary = (interactions, getMedicationName) => {
  if (!interactions || !Array.isArray(interactions) || interactions.length === 0) {
    return 'No interactions found';
  }

  const count = interactions.length;
  const aggregateRisk = calculateAggregateRisk(interactions);
  
  if (count === 1) {
    const interaction = interactions[0];
    const medicationName = getMedicationName ? 
      getMedicationName(interaction.medication_in_rxcui) : 
      'Unknown medication';
    return `${interaction.risk} risk with ${medicationName}`;
  }

  // Multiple interactions
  const riskCounts = interactions.reduce((counts, interaction) => {
    const risk = interaction.risk || 'Unknown';
    counts[risk] = (counts[risk] || 0) + 1;
    return counts;
  }, {});

  const riskSummary = Object.entries(riskCounts)
    .filter(([risk, count]) => count > 0 && risk !== 'Unknown')
    .map(([risk, count]) => `${count} ${risk}`)
    .join(', ');

  return `${count} interactions (${riskSummary}) - Overall: ${aggregateRisk}`;
};

/**
 * Formats detailed interaction list for tooltips or modals
 * @param {Array} interactions - Array of interaction objects
 * @param {Function} getMedicationName - Function to get friendly medication name from rxcui
 * @returns {Array} - Array of formatted interaction objects for display
 */
export const formatInteractionDetails = (interactions, getMedicationName) => {
  if (!interactions || !Array.isArray(interactions)) {
    return [];
  }

  return interactions
    .filter(interaction => interaction && interaction.risk)
    .map(interaction => ({
      risk: interaction.risk,
      medicationName: getMedicationName ? 
        getMedicationName(interaction.medication_in_rxcui) : 
        'Unknown medication',
      reasoning: interaction.reasoning || 'No details available',
      medication_in_rxcui: interaction.medication_in_rxcui
    }))
    .sort((a, b) => {
      // Sort by risk priority (High first, then Medium, then Low)
      const aPriority = RISK_PRIORITY[a.risk] || 0;
      const bPriority = RISK_PRIORITY[b.risk] || 0;
      return bPriority - aPriority;
    });
};

/**
 * Creates the enhanced interaction data structure for supplements
 * @param {Array} supplementInteractions - Array of interactions for a single supplement
 * @param {Function} getMedicationName - Function to get friendly medication name from rxcui
 * @returns {Object} - Enhanced interaction object with primary and all interactions
 */
export const createEnhancedInteractionData = (supplementInteractions, getMedicationName) => {
  if (!supplementInteractions || supplementInteractions.length === 0) {
    return {
      has_interaction: false,
      risk: null,
      reasoning: 'No significant interactions found',
      interacting_medication: null,
      primaryInteraction: null,
      allInteractions: [],
      interactionCount: 0,
      aggregateRisk: 'None'
    };
  }

  const primaryInteraction = selectHighestRiskInteraction(supplementInteractions);
  const aggregateRisk = calculateAggregateRisk(supplementInteractions);
  const formattedInteractions = formatInteractionDetails(supplementInteractions, getMedicationName);

  return {
    has_interaction: true,
    // Backward compatibility fields (using primary interaction)
    risk: primaryInteraction.risk,
    reasoning: primaryInteraction.reasoning,
    interacting_medication: getMedicationName ? 
      getMedicationName(primaryInteraction.medication_in_rxcui) : 
      'Unknown medication',
      medication_in_rxcui: primaryInteraction.medication_in_rxcui,
    
    // Enhanced fields for multiple interactions
    primaryInteraction: {
      risk: primaryInteraction.risk,
      reasoning: primaryInteraction.reasoning,
      interacting_medication: getMedicationName ? 
        getMedicationName(primaryInteraction.medication_in_rxcui) : 
        'Unknown medication',
      medication_in_rxcui: primaryInteraction.medication_in_rxcui
    },
    allInteractions: formattedInteractions,
    interactionCount: supplementInteractions.length,
    aggregateRisk: aggregateRisk
  };
};

/**
 * Gets risk color for UI display
 * @param {string} risk - Risk level ('High', 'Medium', 'Low')
 * @returns {string} - CSS color value
 */
export const getRiskColor = (risk) => {
  switch (risk?.toLowerCase()) {
    case 'high':
      return '#d32f2f'; // Red
    case 'medium':
      return '#f57c00'; // Orange
    case 'low':
      return '#ffa000'; // Yellow/Amber
    default:
      return '#666666'; // Gray
  }
};

/**
 * Gets risk emoji for display
 * @param {string} risk - Risk level ('High', 'Medium', 'Low')
 * @returns {string} - Emoji representation
 */
export const getRiskEmoji = (risk) => {
  switch (risk?.toLowerCase()) {
    case 'high':
      return '🔴';
    case 'medium':
      return '🟡';
    case 'low':
      return '🟡';
    default:
      return '⚪';
  }
};
